import { Th, Td } from '@/components/atoms/TableCells';
import { usBookCurrent } from '@/lib/polymarketUs/gameMapping';
import type { UsMarketBookData } from '@/lib/polymarketUs/marketClient';
import type { BookLevel } from '../types';
import { formatUsContractCounter, type UsMarketStatistics } from '@/lib/polymarketUs/statistics';
import { formatVolume } from '../ladder/helpers';
import { cn } from '@/lib/infra/utils';

export const US_VOLUME_UNAVAILABLE = 'Traded contracts are not supplied by the current US source. Resting depth is not traded volume.';
export const US_VOLUME_DESCRIPTION = 'Venue-reported traded contracts; accumulation/reset window unspecified. Not dollar, lifetime or 24-hour volume. The cell details retain the original source observation.';
export const US_VELOCITY_UNAVAILABLE = 'US best-bid depth change over one minute is not available.';
const dollars = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 2 });

/** The shared binary book's resting dollars, only when BOTH native sides are
 * complete and current. Neither a 25-level excerpt nor another market may
 * become a market-wide total. The caller supplies its already-open book;
 * this presentation adds no readers or timers. */
export function usBookLiquidity(book: UsMarketBookData | null | undefined, slug: string | null, now: number): number | null {
  if (!book || !slug || book.marketSlug !== slug || !usBookCurrent(book, now)
    || !book.yes.complete || !book.no.complete || book.yes.truncated || book.no.truncated) return null;
  let total = 0;
  for (const side of [book.yes, book.no]) for (const level of side.bids) {
    const price = Number(level.priceUsd), size = Number(level.size);
    if (!Number.isFinite(price) || price < 0 || price > 1 || !Number.isFinite(size) || size < 0) return null;
    total += price * size;
  }
  return Number.isFinite(total) ? total : null;
}

/** Same metric seats as international Poly. Missing source fields stay
 * visible as unavailable, never silently disappear or become zero. */
export function UsMetricHeaders({ compact = false }: { compact?: boolean }) {
  const density = compact && 'px-3 py-2 text-[10px]';
  return <>
    <Th className={cn('w-24 text-right', density)} title={US_VOLUME_DESCRIPTION}>US Vol</Th>
    <Th className={cn('w-24 text-right', density)} title="Market rows: shared total dollars resting in the complete US book. Expanded ladder rows: price × quantity resting at that level.">US Liq</Th>
    <Th className={cn('w-16 text-right', density)} title={US_VELOCITY_UNAVAILABLE}>US Δ/m</Th>
  </>;
}

export function UsMetricCells({ book, statistics, slug = null, now = 0, final = false }: {
  book?: UsMarketBookData | null; statistics?: UsMarketStatistics | null; slug?: string | null; now?: number; final?: boolean;
}) {
  const liquidity = final ? null : usBookLiquidity(book, slug, now);
  const counter = !final && statistics?.marketSlug === slug ? statistics : null;
  const volume = counter?.sharesTraded ?? null;
  const volumeTitle = counter && volume !== null
    ? `${volume} contracts traded. Accumulation/reset window unspecified. Last observed ${new Date(counter.observedAtMs).toISOString()} (${counter.source}); later quote activity does not refresh this counter.`
    : US_VOLUME_UNAVAILABLE;
  const liqTitle = liquidity !== null
    ? 'Shared market-wide book liquidity — one binary market underlies both outcomes; this is not an independent per-outcome total.'
    : book?.marketSlug === slug && usBookCurrent(book, now) && (!book.yes.complete || !book.no.complete || book.yes.truncated || book.no.truncated)
      ? 'US total liquidity unavailable: the supplied depth is incomplete.'
      : 'US total liquidity unavailable without a complete current book.';
  return <>
    <Td className="text-muted-foreground text-right font-mono text-sm" data-testid="us-total-volume" title={volumeTitle}>{final ? '' : volume === null ? '—' : formatUsContractCounter(volume)}</Td>
    <Td className="text-muted-foreground text-right font-mono text-sm" data-testid="us-total-liquidity" title={liqTitle}>
      {final ? '' : liquidity === null ? '—' : <>{dollars.format(liquidity)}<sup className="ml-0.5 text-[9px]">ˢ</sup></>}
    </Td>
    <Td className="text-muted-foreground text-right font-mono text-sm" data-testid="us-depth-velocity" title={US_VELOCITY_UNAVAILABLE}>{final ? '' : '—'}</Td>
  </>;
}

export function UsLadderMetricCaption({ shown }: { shown: boolean }) {
  return <>
    <Td className="py-0.5 text-right"><span className="text-muted-foreground/70 whitespace-nowrap text-[9px] font-semibold uppercase">{shown ? 'Resting Qty' : ' '}</span></Td>
    <Td className="py-0.5 text-right"><span className="text-muted-foreground/70 whitespace-nowrap text-[9px] font-semibold uppercase">{shown ? 'Resting $' : ' '}</span></Td>
    <Td className="py-0.5">{' '}</Td>
  </>;
}

export function UsLadderMetricCells({ level }: { level?: BookLevel | null }) {
  return <>
    <Td className="text-muted-foreground py-0.5 text-right font-mono text-xs" title="Resting Qty — contracts resting at this price level, not traded volume" data-testid="us-resting-quantity">
      {level ? formatVolume(level.size) : ' '}
    </Td>
    <Td className="text-muted-foreground py-0.5 text-right font-mono text-xs" title="Resting $ — price × quantity resting at this price level" data-testid="us-resting-dollars">
      {level ? dollars.format(level.priceCents * level.size / 100) : ' '}
    </Td>
    <Td className="py-0.5">{' '}</Td>
  </>;
}
